import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { ReceivedCondition } from '@/services/equipment-intake.service';
import { CONDITION_ITEMS, type ConditionKey } from '../intake-form.constants';

/**
 * The received-condition checklist — one implementation, two screens.
 *
 * Lifted verbatim out of the Equipment Intake Workspace so the edit screen
 * captures the identical structure rather than growing a second checklist that
 * could drift in labels, ordering or shape. The markup is unchanged, so the
 * Workspace renders exactly as it did before.
 *
 * Purely observational: this records what the machine looked like on arrival.
 * It never gates, blocks or classifies anything.
 */

export interface ConditionFlagState {
  present: boolean;
  notes: string;
}

export interface ReceivedConditionState {
  physicalDamage: ConditionFlagState;
  displayCondition: ConditionFlagState;
  waterDamage: ConditionFlagState;
  brokenSeal: ConditionFlagState;
  missingParts: ConditionFlagState;
  tamperEvidence: ConditionFlagState;
  generalRemarks: string;
}

export function emptyReceivedCondition(): ReceivedConditionState {
  const flag = (): ConditionFlagState => ({ present: false, notes: '' });
  return {
    physicalDamage: flag(),
    displayCondition: flag(),
    waterDamage: flag(),
    brokenSeal: flag(),
    missingParts: flag(),
    tamperEvidence: flag(),
    generalRemarks: '',
  };
}

/** Form state → the shape persisted in receivedConditionJson. */
export function toReceivedConditionPayload(state: ReceivedConditionState): ReceivedCondition {
  const toFlag = (f: ConditionFlagState) => ({
    present: f.present,
    notes: f.notes.trim() || undefined,
  });
  return {
    physicalDamage: toFlag(state.physicalDamage),
    displayCondition: toFlag(state.displayCondition),
    waterDamage: toFlag(state.waterDamage),
    brokenSeal: toFlag(state.brokenSeal),
    missingParts: toFlag(state.missingParts),
    tamperEvidence: toFlag(state.tamperEvidence),
    generalRemarks: state.generalRemarks.trim() || undefined,
  };
}

/**
 * Persisted shape → form state, for editing an existing intake.
 *
 * A missing or malformed value yields the empty checklist rather than throwing:
 * an intake created before the checklist existed — or by bulk import, which
 * records no condition — must still open in the editor with all six items
 * present and unset, ready to be filled in. That is the whole point of Phase 3
 * for bulk intakes.
 */
export function fromReceivedCondition(value: unknown): ReceivedConditionState {
  const empty = emptyReceivedCondition();
  if (!value || typeof value !== 'object') return empty;
  const source = value as Record<string, { present?: unknown; notes?: unknown } | undefined>;

  const state = { ...empty };
  for (const { key } of CONDITION_ITEMS) {
    const flag = source[key];
    state[key as ConditionKey] = {
      present: flag?.present === true,
      notes: typeof flag?.notes === 'string' ? flag.notes : '',
    };
  }
  const remarks = (value as { generalRemarks?: unknown }).generalRemarks;
  state.generalRemarks = typeof remarks === 'string' ? remarks : '';
  return state;
}

export function ReceivedConditionChecklist({
  value,
  onChange,
  disabled,
}: {
  value: ReceivedConditionState;
  onChange: (v: ReceivedConditionState) => void;
  disabled: boolean;
}) {
  const toggle = (key: ConditionKey) =>
    onChange({ ...value, [key]: { ...value[key], present: !value[key].present } });
  const setNotes = (key: ConditionKey, notes: string) =>
    onChange({ ...value, [key]: { ...value[key], notes } });

  return (
    <div className="space-y-2">
      {CONDITION_ITEMS.map(({ key, label }) => (
        <div key={key} className="rounded-lg border border-border bg-card p-3 space-y-2">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={value[key].present}
              onChange={() => toggle(key)}
              disabled={disabled}
              className="h-4 w-4 accent-primary"
            />
            <span className="text-sm font-medium text-foreground">{label}</span>
          </label>
          {value[key].present && (
            <Input
              value={value[key].notes}
              onChange={(e) => setNotes(key, e.target.value)}
              placeholder={`Describe the ${label.toLowerCase()}…`}
              disabled={disabled}
              className="text-sm h-8"
            />
          )}
        </div>
      ))}
      <FormField label="General Remarks" htmlFor="rc-general-remarks">
        <Textarea
          id="rc-general-remarks"
          value={value.generalRemarks}
          onChange={(e) => onChange({ ...value, generalRemarks: e.target.value })}
          placeholder="Overall condition remarks…"
          rows={2}
          disabled={disabled}
        />
      </FormField>
    </div>
  );
}
